// Stammdaten der eingeloggten Praxis bearbeiten.
//
// Felder:
//   - Basis (Name, Telefon, Adresse, Öffnungszeiten, Fachrichtung)
//   - booking_url — wenn gesetzt, erscheint in der App "Termin online buchen"-Button
//   - Website
//   - Notiz (intern)
//   - Passwort

import { useEffect, useState } from 'react';
import { BertsyQrCode } from '../components/BertsyQrCode';
import { getMe, praxisQrPayload, updateMe, type Fachrichtung, type Praxis } from '../api';

const FACHRICHTUNGEN: Array<{ id: Fachrichtung; label: string }> = [
  { id: 'allgemeinmedizin', label: 'Allgemeinmedizin / Hausarzt' },
  { id: 'hno', label: 'HNO' },
  { id: 'kinderarzt', label: 'Kinderarzt' },
  { id: 'internist', label: 'Internist' },
  { id: 'augenarzt', label: 'Augenarzt' },
  { id: 'zahnarzt', label: 'Zahnarzt' },
  { id: 'orthopaede', label: 'Orthopäde' },
  { id: 'gynaekologe', label: 'Gynäkologe' },
  { id: 'hautarzt', label: 'Hautarzt' },
  { id: 'urologe', label: 'Urologe' },
  { id: 'psychiater', label: 'Psychiater' },
  { id: 'neurologe', label: 'Neurologe' },
  { id: 'andere', label: 'Andere' },
];

export default function MeinePraxis() {
  const [praxis, setPraxis] = useState<Praxis | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [telefon, setTelefon] = useState('');
  const [adresse, setAdresse] = useState('');
  const [notiz, setNotiz] = useState('');
  const [website, setWebsite] = useState('');
  const [bookingUrl, setBookingUrl] = useState('');
  const [fachrichtung, setFachrichtung] = useState<Fachrichtung | ''>('');
  const [oeffnungszeiten, setOeffnungszeiten] = useState('');
  const [chatDisabled, setChatDisabled] = useState(false);
  const [urlaubVon, setUrlaubVon] = useState('');
  const [urlaubBis, setUrlaubBis] = useState('');
  const [urlaubNachricht, setUrlaubNachricht] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  useEffect(() => {
    getMe()
      .then((r) => {
        setPraxis(r.praxis);
        setName(r.praxis.name);
        setTelefon(r.praxis.telefon ?? '');
        setAdresse(r.praxis.adresse ?? '');
        setNotiz(r.praxis.notiz ?? '');
        setWebsite(r.praxis.website ?? '');
        setBookingUrl(r.praxis.booking_url ?? '');
        setFachrichtung(r.praxis.fachrichtung ?? '');
        setOeffnungszeiten(r.praxis.oeffnungszeiten ?? '');
        setChatDisabled(r.praxis.chat_disabled ?? false);
        setUrlaubVon(r.praxis.urlaub_von ?? '');
        setUrlaubBis(r.praxis.urlaub_bis ?? '');
        setUrlaubNachricht(r.praxis.urlaub_nachricht ?? '');
      })
      .catch((e) => setErr(e.message));
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password && password !== password2) {
      setErr('Passwörter stimmen nicht überein.');
      return;
    }
    setLoading(true);
    setErr(null);
    setSaved(false);
    try {
      const r = await updateMe({
        name,
        telefon: telefon || null,
        adresse: adresse || null,
        notiz: notiz || null,
        website: website || null,
        booking_url: bookingUrl || null,
        fachrichtung: (fachrichtung || null) as any,
        oeffnungszeiten: oeffnungszeiten || null,
        chat_disabled: chatDisabled,
        urlaub_von: urlaubVon || null,
        urlaub_bis: urlaubBis || null,
        urlaub_nachricht: urlaubNachricht || null,
        password: password || undefined,
      });
      setPraxis(r.praxis);
      setPassword('');
      setPassword2('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!praxis) return <p className="page-sub">Wird geladen…</p>;

  return (
    <>
      <h1 className="page-title">Meine Praxis</h1>
      <p className="page-sub">Stammdaten Ihrer Praxis pflegen — werden in der Bertsy-App Ihren Patienten angezeigt.</p>

      <div className="card" style={{ marginBottom: 16, background: 'var(--bg)', borderLeft: '4px solid var(--primary)' }}>
        <h4 className="section-title">Praxis-ID & Praxis-Code</h4>
        <div style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>Praxis-ID:</strong> <code>{praxis.id}</code>
        </div>
        <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
          Praxis-Code (für QR + manuelle Eingabe in der App)
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <code style={{
            flex: 1, padding: '8px 10px', background: '#fff',
            border: '1px solid #DDE9E8', borderRadius: 4,
            fontSize: 12, wordBreak: 'break-all', userSelect: 'all',
          }}>
            {praxis.qr_token}
          </code>
          <button
            type="button"
            className="btn btn-primary"
            style={{ padding: '8px 12px', fontSize: 12 }}
            onClick={async () => {
              try { await navigator.clipboard.writeText(praxis.qr_token); alert('Code kopiert.'); }
              catch { alert('Bitte manuell markieren und kopieren.'); }
            }}
          >
            Kopieren
          </button>
        </div>
        <p style={{ fontSize: 12, color: '#5F6B68', marginTop: 8 }}>
          Patienten scannen den QR-Code unten — oder geben den Praxis-Code manuell in der App ein.
          Code-Rotation nur über Bertsy-Admin möglich.
        </p>

        {/* QR-Code mit Bertsy-Logo in der Mitte. Identisch zum Admin-Dashboard. */}
        <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #DDE9E8' }}>
          <div style={{ fontSize: 11, color: '#5F6B68', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, textAlign: 'center' }}>
            QR-Code für die Praxis (drucken & aushängen)
          </div>
          <BertsyQrCode
            payload={praxisQrPayload(praxis)}
            size={260}
            downloadName={`bertsy-qr-${praxis.id}`}
            caption={
              <>
                <strong>{praxis.name}</strong>
                {praxis.adresse && <><br />{praxis.adresse}</>}
              </>
            }
          />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h4 className="section-title" style={{ marginTop: 0 }}>📌 Bertsy auf Ihrer Webseite einbinden</h4>
        <p style={{ fontSize: 13, color: '#5F6B68', margin: '0 0 12px' }}>
          Fügen Sie folgendes Script auf Ihrer Praxis-Webseite ein. Patienten sehen dann einen
          "Mit Bertsy verbinden"-Button — Klick führt entweder direkt in die App (falls installiert)
          oder in den App-Store.
        </p>
        <pre style={{
          background: '#1D1D1B', color: '#9FE7E3', padding: 12, borderRadius: 6,
          fontSize: 12, overflow: 'auto', userSelect: 'all',
        }}>{`<script src="https://cdn.bertsy.de/bertsy-embed.js"
  data-praxis-code="${praxis.qr_token}"
  data-position="bottom-right"
  defer></script>`}</pre>
        <button
          type="button"
          className="btn-secondary"
          style={{ marginTop: 8 }}
          onClick={async () => {
            const snippet = `<script src="https://cdn.bertsy.de/bertsy-embed.js" data-praxis-code="${praxis.qr_token}" data-position="bottom-right" defer></script>`;
            try { await navigator.clipboard.writeText(snippet); alert('Snippet kopiert.'); } catch { alert('Bitte manuell kopieren.'); }
          }}
        >
          📋 Snippet kopieren
        </button>
      </div>

      <form className="card" onSubmit={onSubmit}>
        <h4 className="section-title" style={{ marginTop: 0 }}>Basis-Daten</h4>
        <div className="field">
          <label>Praxis-Name *</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label>E-Mail (nur Bertsy-Admin kann diese ändern)</label>
          <input type="email" value={praxis.email} disabled style={{ background: '#F2F8F8', color: '#5F6B68' }} />
        </div>
        <div className="field">
          <label>Telefon</label>
          <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="z. B. 0711 123 456" />
        </div>
        <div className="field">
          <label>Adresse</label>
          <input type="text" value={adresse} onChange={(e) => setAdresse(e.target.value)} placeholder="Straße Hausnr., PLZ Ort" />
        </div>

        <div className="field">
          <label>Fachrichtung</label>
          <select
            value={fachrichtung}
            onChange={(e) => setFachrichtung(e.target.value as any)}
            style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, background: '#fff' }}
          >
            <option value="">— Bitte wählen —</option>
            {FACHRICHTUNGEN.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          <p style={{ fontSize: 11, color: '#5F6B68', margin: '4px 0 0' }}>
            Wird in der Patient-App unter "Meine Praxis" angezeigt.
          </p>
        </div>

        <div className="field">
          <label>Öffnungszeiten (Freitext)</label>
          <input type="text" value={oeffnungszeiten} onChange={(e) => setOeffnungszeiten(e.target.value)} placeholder="Mo–Fr 8:00–18:00, Sa 9:00–12:00" />
        </div>

        <div className="field">
          <label>Website</label>
          <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://www.ihre-praxis.de" />
        </div>

        <div className="field">
          <label>Online-Terminbuchungs-URL</label>
          <input type="url" value={bookingUrl} onChange={(e) => setBookingUrl(e.target.value)} placeholder="https://www.doctolib.de/.../ihre-praxis" />
          <p style={{ fontSize: 11, color: '#5F6B68', margin: '4px 0 0' }}>
            Wenn gesetzt: Patienten sehen in der App einen <strong>„Termin online buchen"</strong>-Button, der direkt auf
            diese URL führt. Sie können hier einen Doctolib/Jameda/Samedi-Link einfügen — oder einen Link auf Ihren eigenen Buchungs-Kalender.
          </p>
        </div>

        <h4 className="section-title" style={{ marginTop: 24 }}>Chat-Funktion</h4>
        <p style={{ fontSize: 12, color: '#5F6B68', margin: '0 0 12px' }}>
          Steuern Sie, ob Patienten Sie über den Chat erreichen können.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={chatDisabled}
            onChange={(e) => setChatDisabled(e.target.checked)}
            style={{ width: 18, height: 18 }}
          />
          <span style={{ fontSize: 14 }}>
            <strong>Chat dauerhaft deaktivieren</strong> — Patienten sehen "Praxis bietet diese Funktion momentan nicht an"
          </span>
        </label>

        <h4 className="section-title" style={{ marginTop: 20 }}>Urlaubsmodus / Abwesenheit</h4>
        <p style={{ fontSize: 12, color: '#5F6B68', margin: '0 0 12px' }}>
          Im Urlaubszeitraum ist der Chat automatisch deaktiviert. Patienten sehen Ihre Nachricht.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>Urlaub von</label>
            <input type="date" value={urlaubVon} onChange={(e) => setUrlaubVon(e.target.value)} />
          </div>
          <div className="field">
            <label>Urlaub bis</label>
            <input type="date" value={urlaubBis} onChange={(e) => setUrlaubBis(e.target.value)} min={urlaubVon || undefined} />
          </div>
        </div>
        <div className="field">
          <label>Urlaubs-Nachricht für Patienten (optional)</label>
          <textarea
            rows={2}
            value={urlaubNachricht}
            onChange={(e) => setUrlaubNachricht(e.target.value)}
            placeholder="Wir sind vom 15.06. bis 28.06. im Urlaub. Bei dringenden Fragen wenden Sie sich bitte an die Vertretungspraxis Dr. Müller (0711 12345)."
            maxLength={500}
          />
        </div>

        <h4 className="section-title" style={{ marginTop: 24 }}>Notiz (intern)</h4>
        <div className="field">
          <label>Interne Notiz — sehen nur Sie selbst</label>
          <textarea rows={2} value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </div>

        <h4 className="section-title" style={{ marginTop: 24 }}>Passwort ändern</h4>
        <div className="field">
          <label>Neues Passwort (mindestens 8 Zeichen, leer = nicht ändern)</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={password ? 8 : 0} autoComplete="new-password" />
        </div>
        <div className="field">
          <label>Neues Passwort wiederholen</label>
          <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} autoComplete="new-password" />
        </div>

        {err && <div className="error" style={{ marginBottom: 8 }}>{err}</div>}
        {saved && <div style={{ color: '#2E7D32', fontSize: 13, marginBottom: 8 }}>✓ Gespeichert</div>}

        <button className="btn btn-primary" type="submit" disabled={loading}>
          {loading ? 'Speichern…' : 'Speichern'}
        </button>
      </form>
    </>
  );
}
